import React, { useMemo, useState } from 'react';
import { ArrowRightLeftIcon, LandmarkIcon, CalendarClockIcon, HistoryIcon, ShieldCheckIcon, ChevronRightIcon, SearchIcon, StarIcon, PlusIcon, RepeatIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { ScreenHeader } from './ScreenHeader';
export type TransferAction = 'own' | 'another' | 'schedule' | 'history';
type Favourite = {
  id: string;
  name: string;
  initials: string;
  bank: string;
  color: string;
};
type RecentTransfer = {
  id: string;
  name: string;
  initials: string;
  bank: string;
  color: string;
  amount: string;
  when: string;
};
const FAVOURITES: Favourite[] = [{
  id: 'f1',
  name: 'Nimal Silva',
  initials: 'NS',
  bank: 'Commercial Bank',
  color: '#009AC7'
}, {
  id: 'f2',
  name: 'Anoma Perera',
  initials: 'AP',
  bank: 'Sampath Bank',
  color: '#8B5CF6'
}, {
  id: 'f3',
  name: 'Sunil F.',
  initials: 'SF',
  bank: "People's Bank",
  color: '#10B981'
}, {
  id: 'f4',
  name: 'Ravi K.',
  initials: 'RK',
  bank: 'BOC',
  color: '#F59E0B'
}, {
  id: 'f5',
  name: 'Tharushi',
  initials: 'TM',
  bank: 'HNB',
  color: '#EF4444'
}];
const RECENT: RecentTransfer[] = [{
  id: 'r1',
  name: 'Nimal Silva',
  initials: 'NS',
  bank: 'Commercial Bank · 8001 •••• 2245',
  color: '#009AC7',
  amount: 'LKR 12,500.00',
  when: 'Yesterday · 4:12 PM'
}, {
  id: 'r2',
  name: 'Landlord — Mr. Fernando',
  initials: 'MF',
  bank: 'Sampath Bank · monthly rent',
  color: '#8B5CF6',
  amount: 'LKR 45,000.00',
  when: '24 May · 9:02 AM'
}, {
  id: 'r3',
  name: 'Sunil Perera',
  initials: 'SP',
  bank: "People's Bank · 0567 •••• 1190",
  color: '#10B981',
  amount: 'LKR 4,800.00',
  when: '21 May · 8:48 PM'
}, {
  id: 'r4',
  name: 'Anoma Perera',
  initials: 'AP',
  bank: 'Sampath Bank · 4421 •••• 7732',
  color: '#8B5CF6',
  amount: 'LKR 8,200.00',
  when: '19 May · 11:14 AM'
}];
export function TransferScreen({
  onBack,
  onSelect
}: {
  onBack: () => void;
  onSelect: (action: TransferAction) => void;
}) {
  const [query, setQuery] = useState('');
  const filteredRecent = useMemo(() => {
    if (!query.trim()) return RECENT;
    const q = query.toLowerCase();
    return RECENT.filter(r => r.name.toLowerCase().includes(q) || r.bank.toLowerCase().includes(q));
  }, [query]);
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-822">
      <StatusBar data-id="element-823" />
      <ScreenHeader title="Money Transfer" onBack={onBack} data-id="element-824" />
      <div className="flex-1 overflow-y-auto px-5 pb-6" data-id="element-825">
        {/* Balance hero — compact */}
        <div className="relative overflow-hidden rounded-2xl p-4 mb-4" style={{
        background: 'linear-gradient(135deg, #009AC7 0%, #007FA5 100%)',
        color: '#FFFFFF',
        boxShadow: '0 12px 28px -16px rgba(0,154,199,0.5)'
      }} data-id="element-826">
          <div aria-hidden="true" className="pointer-events-none absolute -right-8 -bottom-8 rounded-full blur-2xl" style={{
          width: 140,
          height: 140,
          background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 70%)'
        }} data-id="element-827" />
          <div className="relative flex items-center justify-between" data-id="element-828">
            <div data-id="element-829">
              <div style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              opacity: 0.9
            }} data-id="element-830">
                AVAILABLE TO TRANSFER
              </div>
              <div className="mt-1 tracking-tight" style={{
              fontSize: 24,
              fontWeight: 700
            }} data-id="element-831">
                LKR 482,650.00
              </div>
              <div className="mt-0.5" style={{
              fontSize: 11.5,
              opacity: 0.85,
              fontWeight: 500
            }} data-id="element-832">
                Savings · 0123 •••• 8910
              </div>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2.5 px-4 rounded-2xl bg-white mb-4 transition-all" style={{
        minHeight: 50,
        border: query ? '1.5px solid #009AC7' : '1.5px solid #E5EAF0',
        boxShadow: query ? '0 0 0 4px rgba(0,154,199,0.10)' : 'none'
      }} data-id="element-833">
          <SearchIcon size={18} style={{
          color: query ? '#009AC7' : '#94A3B8'
        }} data-id="element-834" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search recipients, banks, or transfers" className="flex-1 min-w-0 bg-transparent outline-none" style={{
          fontSize: 14,
          fontWeight: 500,
          color: '#1A1A1A'
        }} data-id="element-835" />
        </div>

        {/* Favourites — horizontal scroll */}
        <div className="mb-5" data-id="element-836">
          <SectionHeading title="Favourites" action="Manage" data-id="element-837" />
          <div className="flex gap-3 overflow-x-auto -mx-5 px-5 pb-1" style={{
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-838">
            <button type="button" onClick={() => onSelect('another')} className="shrink-0 flex flex-col items-center gap-1.5 transition-transform active:scale-[0.95]" style={{
            width: 64
          }} data-id="element-839">
              <span className="flex items-center justify-center rounded-full" style={{
              width: 52,
              height: 52,
              backgroundColor: '#FFFFFF',
              border: '1.5px dashed #009AC7',
              color: '#009AC7'
            }} data-id="element-840">
                <PlusIcon size={20} strokeWidth={2.5} data-id="element-841" />
              </span>
              <span className="text-center" style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: '#475569',
              lineHeight: 1.2
            }} data-id="element-842">
                Add new
              </span>
            </button>
            {FAVOURITES.map(f => <button key={f.id} type="button" onClick={() => onSelect('another')} className="shrink-0 flex flex-col items-center gap-1.5 transition-transform active:scale-[0.95]" style={{
            width: 64
          }} data-id="element-843">
                <span className="relative inline-flex" data-id="element-844">
                  <span className="flex items-center justify-center rounded-full" style={{
                width: 52,
                height: 52,
                backgroundColor: `${f.color}15`,
                color: f.color,
                border: `1.5px solid ${f.color}30`
              }} data-id="element-845">
                    <span style={{
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '0.02em'
                }} data-id="element-846">
                      {f.initials}
                    </span>
                  </span>
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full" style={{
                width: 18,
                height: 18,
                backgroundColor: '#FACC15',
                border: '2px solid #F4F7FA'
              }} aria-label="Favourite" data-id="element-847">
                    <StarIcon size={9} color="#FFFFFF" strokeWidth={3} fill="#FFFFFF" data-id="element-848" />
                  </span>
                </span>
                <span className="text-center w-full truncate" style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: '#1A1A1A',
              lineHeight: 1.2
            }} data-id="element-849">
                  {f.name.split(' ')[0]}
                </span>
              </button>)}
          </div>
        </div>

        {/* Transfer methods — 2x2 grid */}
        <SectionHeading title="Transfer methods" data-id="element-850" />
        <div className="grid grid-cols-2 gap-2.5 mb-5" data-id="element-851">
          <MethodCard icon={<ArrowRightLeftIcon size={20} data-id="element-853" />} title="Own Account" sub="Between your accounts" onClick={() => onSelect('own')} data-id="element-852" />
          <MethodCard icon={<LandmarkIcon size={20} data-id="element-855" />} title="Another Bank" sub="To other Sri Lankan banks" onClick={() => onSelect('another')} highlight data-id="element-854" />
          <MethodCard icon={<CalendarClockIcon size={20} data-id="element-857" />} title="Schedule" sub="Upcoming transfers" onClick={() => onSelect('schedule')} data-id="element-856" />
          <MethodCard icon={<HistoryIcon size={20} data-id="element-859" />} title="History" sub="All past transfers" onClick={() => onSelect('history')} data-id="element-858" />
        </div>

        {/* Recent transfers list */}
        <SectionHeading title="Recent transfers" action="See all" onAction={() => onSelect('history')} data-id="element-860" />
        <div className="rounded-2xl bg-white overflow-hidden" style={{
        border: '1px solid #E5EAF0'
      }} data-id="element-861">
          {filteredRecent.length === 0 ? <div className="text-center py-6 px-4" data-id="element-862">
              <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#1A1A1A'
          }} data-id="element-863">
                No matches for "{query}"
              </div>
              <div className="mt-1" style={{
            fontSize: 12,
            color: '#64748B'
          }} data-id="element-864">
                Try a different name or bank.
              </div>
            </div> : filteredRecent.map((r, i) => <button key={r.id} type="button" onClick={() => onSelect('another')} className="w-full flex items-center gap-3 px-4 py-3 transition-colors active:bg-neutral-50 text-left" style={{
          borderBottom: i === filteredRecent.length - 1 ? 'none' : '1px solid #F1F5F9'
        }} data-id="element-865">
                <span className="shrink-0 flex items-center justify-center rounded-xl" style={{
            width: 40,
            height: 40,
            backgroundColor: `${r.color}15`,
            color: r.color
          }} data-id="element-866">
                  <span style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.02em'
            }} data-id="element-867">
                    {r.initials}
                  </span>
                </span>
                <div className="flex-1 min-w-0" data-id="element-868">
                  <div className="truncate" style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#1A1A1A'
            }} data-id="element-869">
                    {r.name}
                  </div>
                  <div className="mt-0.5 truncate" style={{
              fontSize: 11.5,
              color: '#64748B',
              fontWeight: 500
            }} data-id="element-870">
                    {r.bank}
                  </div>
                </div>
                <div className="text-right shrink-0" data-id="element-871">
                  <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#1A1A1A'
            }} data-id="element-872">
                    {r.amount}
                  </div>
                  <div className="mt-0.5 inline-flex items-center gap-1" style={{
              fontSize: 11,
              color: '#009AC7',
              fontWeight: 600
            }} data-id="element-873">
                    <RepeatIcon size={11} strokeWidth={2.5} data-id="element-874" />
                    Repeat
                  </div>
                </div>
              </button>)}
        </div>

        {/* Trust footer */}
        <div className="mt-5 flex items-center justify-center gap-1.5" data-id="element-875">
          <ShieldCheckIcon size={13} style={{
          color: '#009AC7'
        }} data-id="element-876" />
          <span style={{
          fontSize: 11.5,
          color: '#64748B',
          fontWeight: 500
        }} data-id="element-877">
            All transfers are encrypted & monitored
          </span>
        </div>
      </div>
      <div className="flex justify-center pb-2" style={{
      backgroundColor: '#F4F7FA'
    }} data-id="element-878">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-879" />
      </div>
    </div>;
}
function SectionHeading({
  title,
  action,
  onAction
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) {
  return <div className="flex items-center justify-between mb-2.5 px-0.5" data-id="element-880">
      <span style={{
      fontSize: 13,
      fontWeight: 700,
      color: '#1A1A1A',
      letterSpacing: '0.01em'
    }} data-id="element-881">
        {title}
      </span>
      {action && <button type="button" onClick={onAction} style={{
      fontSize: 11.5,
      fontWeight: 700,
      color: '#009AC7'
    }} data-id="element-882">
          {action}
        </button>}
    </div>;
}
function MethodCard({
  icon,
  title,
  sub,
  onClick,
  highlight
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  onClick: () => void;
  highlight?: boolean;
}) {
  return <button type="button" onClick={onClick} className="flex flex-col items-start gap-2 rounded-2xl bg-white text-left transition-all active:scale-[0.98]" style={{
    padding: '14px 14px',
    border: highlight ? '1.5px solid #009AC7' : '1px solid #E5EAF0',
    boxShadow: highlight ? '0 8px 20px -10px rgba(0,154,199,0.35)' : '0 1px 2px rgba(15,23,42,0.03)',
    minHeight: 96
  }} data-id="element-883">
      <div className="flex items-center justify-between w-full" data-id="element-884">
        <span className="flex items-center justify-center rounded-xl" style={{
        width: 38,
        height: 38,
        backgroundColor: highlight ? '#009AC7' : '#E6F7FB',
        color: highlight ? '#FFFFFF' : '#009AC7'
      }} data-id="element-885">
          {icon}
        </span>
        <ChevronRightIcon size={16} style={{
        color: '#94A3B8'
      }} data-id="element-886" />
      </div>
      <div className="w-full" data-id="element-887">
        <div style={{
        fontSize: 13.5,
        fontWeight: 700,
        color: '#1A1A1A'
      }} data-id="element-888">
          {title}
        </div>
        <div className="mt-0.5" style={{
        fontSize: 11,
        color: '#64748B',
        fontWeight: 500,
        lineHeight: 1.4
      }} data-id="element-889">
          {sub}
        </div>
      </div>
    </button>;
}